/**
 * backGuard.ts — ANDROID/BROWSER BACK GUARD (BUG FREEZE™ Stage 2, 2.15.0)
 *
 * Cold starts and returns from external websites often leave the browser with
 * a single history entry, so hardware/browser Back exits WorkRoamer even when
 * the app has internal history. This module arms exactly ONE sentinel history
 * entry ({ wrGuard: true }) and, when that sentinel is consumed at the base of
 * the stack (null state + unchanged pathname), resolves Back against the
 * internal NavigationHistoryService stack instead of exiting.
 *
 * SINGLETON: installBackGuard() is idempotent at module level — Shell mounts
 * per protected route, and re-mounting must NEVER stack additional sentinel
 * entries or duplicate popstate listeners. Normal back/forward traversal
 * (URL changes, or landing back ON the sentinel entry) passes through
 * untouched so wouter keeps full control of routing.
 */

import { navigationHistory } from './NavigationHistoryService';

type Navigate = (path: string) => void;

let installed = false;
let armedPath = '';
let navigateFn: Navigate | null = null;

/** Pure classification of a popstate — exported for regression tests. */
export function classifyPop(
  state: unknown,
  pathname: string,
  currentArmedPath: string,
): 'rearmed' | 'intercept' | 'pass' {
  if (state != null && typeof state === 'object' && (state as { wrGuard?: boolean }).wrGuard) {
    // Landed back ON the sentinel via a normal back — it is armed again.
    return 'rearmed';
  }
  // Sentinel consumed at the base of the stack: same URL, non-guard state.
  return pathname === currentArmedPath ? 'intercept' : 'pass';
}

/**
 * arm(useReplace)
 *
 * useReplace=true  (first install):
 *   Uses replaceState so the sentinel overwrites the current entry instead of
 *   adding an extra one. History stays the same length — the splash / first
 *   page seen after login is NOT added as an additional Back-press destination.
 *   Before fix: [/login, /home, /home(guard)]  — extra entry causes splash to
 *   reappear on Back.
 *   After fix:  [/login, /home(guard)]          — one Back press exits cleanly.
 *
 * useReplace=false (re-arm after intercept):
 *   Uses pushState to re-insert the sentinel after the guard navigated to a
 *   previous in-app route, so the next Back press is still caught.
 */
function arm(useReplace = false): void {
  armedPath = window.location.pathname;
  if (useReplace) {
    window.history.replaceState({ wrGuard: true }, '');
  } else {
    window.history.pushState({ wrGuard: true }, '');
  }
}

/** Latest navigator wins — Shell re-mounts refresh it without reinstalling. */
export function setBackGuardNavigator(fn: Navigate | null): void {
  navigateFn = fn;
}

export function installBackGuard(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  if ((window.history.state as { wrGuard?: boolean } | null)?.wrGuard) {
    // Guard state already present (e.g. page refreshed while sentinel was
    // active). Restore armedPath from the current URL so classifyPop works
    // correctly on the next Back press — without this, armedPath stays '' and
    // 'intercept' never fires even when the pathname matches.
    armedPath = window.location.pathname;
  } else {
    // First install: replace the current entry with the sentinel.
    // replaceState keeps the history stack the same length, so the splash /
    // first protected page is NOT pushed as an extra Back-press destination.
    arm(true);
  }
  window.addEventListener('popstate', (e: PopStateEvent) => {
    navigationHistory.markBackIntent();
    const verdict = classifyPop(e.state, window.location.pathname, armedPath);
    if (verdict === 'rearmed') {
      armedPath = window.location.pathname;
      return;
    }
    if (verdict === 'intercept') {
      const prev = navigationHistory.back();
      if (prev != null && navigateFn != null) {
        navigateFn(prev);
        // Re-arm with pushState after the route paints so the next Back is
        // caught too. (Re-arms always push — only the FIRST install replaces.)
        setTimeout(() => arm(false), 0);
      }
      // No internal history left → let the browser exit naturally.
    }
  });
}

/** Test-only: reset module state so install can be exercised again. */
export function _resetBackGuardForTests(): void {
  installed = false;
  armedPath = '';
  navigateFn = null;
}
